var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { DataContext } from '../../player/DataContext';
import { v4 as uuidv4 } from 'uuid';
import { saveState } from '../../helper/stateManagerHelper';
import { getVisibleMidpoint } from '../../helper/viewportHelper';
import globalStore from '../../store';
import '../../styles/widgets/popup/manipulativePopup.scss';
import { getImageSrc } from '../../helper/imageBlobCache';
var BG_GRID = 55; // Must match BackgroundPatternCanvas GRID_SIZE (55px per grid cell)
// Triangular grid row height: height of an equilateral triangle with side BG_GRID
var TRI_H = (BG_GRID * Math.sqrt(3)) / 2; // ≈ 47.63 px
// Actual SVG viewBox pixel dimensions for each outline shape.
// gridType: 'triangle' → align to triangular grid, show triangle grid on add
// gridType: 'square'   → align to square grid, show square grid on add
// gridType: 'none'     → no grid alignment, no grid shown on add
var SHAPE_DIMS = {
    1: { w: 769.99, h: 666.83, gridType: 'triangle' },
    2: { w: 550, h: 571.56, gridType: 'triangle' },
    3: { w: 825, h: 476.3, gridType: 'triangle' },
    4: { w: 660, h: 572.35, gridType: 'triangle' },
    5: { w: 907.5, h: 714.45, gridType: 'triangle' },
    6: { w: 880.23, h: 571.56, gridType: 'triangle' },
    7: { w: 742.43, h: 571.5, gridType: 'triangle' },
    8: { w: 990.28, h: 476.45, gridType: 'triangle' },
    9: { w: 936, h: 571.56, gridType: 'triangle' },
    10: { w: 934, h: 440, gridType: 'square' },
    11: { w: 825, h: 715, gridType: 'square' },
    12: { w: 933, h: 660, gridType: 'square' },
    13: { w: 880, h: 475.52, gridType: 'triangle' },
    14: { w: 660, h: 571.56, gridType: 'triangle' },
    15: { w: 495, h: 666.8, gridType: 'triangle' },
    16: { w: 822.25, h: 506.49, gridType: 'none' },
    17: { w: 931.05, h: 740.52, gridType: 'none' },
    18: { w: 879.8, h: 601.04, gridType: 'none' },
    19: { w: 666.52, h: 701.26, gridType: 'none' },
    20: { w: 709.34, h: 465.5, gridType: 'none' },
    21: { w: 770, h: 770, gridType: 'square' },
};
// Display order matching the XD design (7 columns × 3 rows)
var SHAPE_ORDER = [1, 2, 7, 10, 13, 16, 19, 3, 5, 8, 11, 14, 17, 20, 4, 6, 9, 12, 15, 18, 21];
var LIBRARY_SHAPES = SHAPE_ORDER.map(function (id) { return ({
    id: id,
    imagePath: "./assets/images/manipulative/patternShape/Shape_".concat(String(id).padStart(2, '0'), ".svg"),
    dims: SHAPE_DIMS[id],
}); });
// Module-level storage: holds the shape IDs the user picked before the confirmation popup.
var pendingOutlineShapes = [];
var PatternShapesManipulativePopup = function () {
    var _a = React.useContext(DataContext), manipulative = _a.manipulative, setManipulative = _a.setManipulative, setManiplulatorComponentVisible = _a.setManiplulatorComponentVisible, setActiveManipulator = _a.setActiveManipulator, setSelectedTool = _a.setSelectedTool;
    var zoomLevel = globalStore.useStoreState(function (state) { return state.playerStore; }).zoomLevel;
    var setPlayerBgPattern = globalStore.useStoreActions(function (state) { return state.playerStore; }).setPlayerBgPattern;
    var _b = useState([]), selectedShapes = _b[0], setSelectedShapes = _b[1];
    var popupRef = useRef(null);
    // Move focus to first button when popup opens
    useEffect(function () {
        var _a;
        var firstBtn = (_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button');
        firstBtn === null || firstBtn === void 0 ? void 0 : firstBtn.focus();
    }, []);
    // Close on Escape — does not affect any existing functionality
    useEffect(function () {
        var handleKey = function (e) { if (e.key === 'Escape')
            onCancel(); };
        window.addEventListener('keydown', handleKey);
        return function () { return window.removeEventListener('keydown', handleKey); };
    }, []);
    var toggleShapeSelection = function (shapeId) {
        setSelectedShapes(function (prev) {
            // Single-select only: deselect if already selected, otherwise select this one
            if (prev.includes(shapeId)) {
                return [];
            }
            else {
                return [shapeId];
            }
        });
    };
    var onDone = function () {
        if (selectedShapes.length === 0) {
            setManiplulatorComponentVisible(false);
            setActiveManipulator('');
            return;
        }
        // If there are any existing shapes on the stage, show small confirmation popup.
        var hasExistingShapes = manipulative.some(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes'; });
        if (hasExistingShapes) {
            pendingOutlineShapes = __spreadArray([], selectedShapes, true);
            setActiveManipulator('clearWorkConfirmation');
            return;
        }
        // No existing shapes — add directly.
        addOutlineShapes(selectedShapes);
    };
    var addOutlineShapes = function (shapes) {
        var _a = getVisibleMidpoint(zoomLevel), basex = _a.x, basey = _a.y;
        var newItems = [];
        var newSelectedToolIds = [];
        var BATCH_OFFSET = 1100;
        shapes.forEach(function (shapeId, index) {
            var shape = LIBRARY_SHAPES.find(function (s) { return s.id === shapeId; });
            if (!shape || !shape.dims)
                return;
            var svgW = shape.dims.w;
            var svgH = shape.dims.h;
            var targetX = basex + index * BATCH_OFFSET;
            var targetY = basey + index * BATCH_OFFSET;
            var snappedX;
            var snappedY;
            var gt = shape.dims.gridType;
            if (gt === 'triangle') {
                var evenRowH = 2 * TRI_H;
                snappedY = Math.round((targetY - svgH / 2) / evenRowH) * evenRowH;
                snappedX = Math.round((targetX - svgW / 2) / BG_GRID) * BG_GRID;
            }
            else if (gt === 'square') {
                snappedX = Math.round((targetX - svgW / 2) / BG_GRID) * BG_GRID;
                snappedY = Math.round((targetY - svgH / 2) / BG_GRID) * BG_GRID;
            }
            else {
                snappedX = targetX - svgW / 2;
                snappedY = targetY - svgH / 2;
            }
            var newItem = {
                type: 'patternShapes',
                data: {
                    id: "patternShapes_".concat(uuidv4().replace(/-/g, ''), "_dummyForRightTool"),
                    type: 'patternShapes',
                    shapeType: 'outline',
                    center: {
                        x: snappedX,
                        y: snappedY,
                    },
                    rotation: 0,
                    // scale:1 = "render at native svgW × svgH size". Snapping uses sw = svgW * scale,
                    // so scale:1 gives the correct pixel width. If outline scaling is added later,
                    // update both this field and the CSS transform in PatternShapesManipulative.tsx.
                    scale: 1,
                    svgW: svgW,
                    svgH: svgH,
                    color: 'WHITE',
                    isOutline: true,
                    gridType: shape.dims.gridType,
                    path: shape.imagePath,
                    lock: { positioning: false, editing: false },
                },
                widgetId: "patternShapes:".concat(uuidv4().replace(/-/g, '')),
            };
            saveState({
                type: newItem.data.id,
                id: newItem.data.id.split('_')[1],
                propertyName: 'create',
                old: JSON.stringify(newItem),
            });
            newItems.push(newItem);
            newSelectedToolIds.push(newItem.data.id);
        });
        // Set the background grid based on the first shape's gridType.
        var firstShape = LIBRARY_SHAPES.find(function (s) { return s.id === shapes[0]; });
        if (firstShape === null || firstShape === void 0 ? void 0 : firstShape.dims) {
            var gt = firstShape.dims.gridType;
            if (gt === 'triangle') {
                setPlayerBgPattern('triangle');
            }
            else if (gt === 'square') {
                setPlayerBgPattern('square');
            }
            else {
                setPlayerBgPattern('none');
            }
        }
        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), newItems, true); });
        setSelectedTool(newSelectedToolIds);
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    var onCancel = function () {
        setSelectedShapes([]);
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    return (_jsxs("div", { ref: popupRef, className: "patternShapesLibraryPopup", role: "dialog", "aria-modal": "true", "aria-labelledby": "patternLibraryTitle", children: [_jsx("span", { id: "patternLibraryTitle", style: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }, children: t('text.patternShapesLibraryAriaLabel') }), _jsx("div", { className: "libraryGrid", children: LIBRARY_SHAPES.map(function (shape, index) { return (_jsxs("button", { className: "libraryShapeOption ".concat(selectedShapes.includes(shape.id) ? 'selected' : ''), onClick: function () { return toggleShapeSelection(shape.id); }, "aria-label": t('text.patternShapeLabel', { id: index + 1 }), "aria-pressed": selectedShapes.includes(shape.id), children: [_jsx("img", { src: getImageSrc(shape.imagePath), alt: t('text.patternShapeLabel', { id: index + 1 }) }), selectedShapes.includes(shape.id) && (_jsx("div", { className: "checkmark", children: _jsx("svg", { viewBox: "0 0 24 24", width: "20", height: "20", children: _jsx("path", { d: "M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z", fill: "#1a2956" }) }) }))] }, shape.id)); }) }), _jsxs("div", { className: "popupActions", children: [_jsx("button", { className: "cancelBtn", onClick: onCancel, children: t('text.cancel') }), _jsx("button", { className: "doneBtn", onClick: onDone, disabled: selectedShapes.length === 0, children: t('text.done') })] })] }));
};
/* ------------------------------------------------------------------ */
/* Reusable confirmation popup layout (heading + 2 buttons + patches) */
/* ------------------------------------------------------------------ */
var PatternConfirmationPopup = function (_a) {
    var heading = _a.heading, cancelLabel = _a.cancelLabel, confirmLabel = _a.confirmLabel, onCancel = _a.onCancel, onConfirm = _a.onConfirm;
    var cancelRef = useRef(null);
    // Move focus to Cancel button when confirmation opens
    useEffect(function () {
        var _a;
        (_a = cancelRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    // Escape cancels the confirmation — does not affect any existing functionality
    useEffect(function () {
        var handleKey = function (e) { if (e.key === 'Escape')
            onCancel(); };
        window.addEventListener('keydown', handleKey);
        return function () { return window.removeEventListener('keydown', handleKey); };
    }, [onCancel]);
    return (_jsxs("div", { className: "clearWorkConfirmContent", role: "dialog", "aria-modal": "true", "aria-labelledby": "patternConfirmTitle", children: [_jsx("h3", { id: "patternConfirmTitle", className: "clearWorkConfirmContent__heading", children: heading }), _jsxs("div", { className: "clearWorkConfirmContent__buttons", children: [_jsx("button", { ref: cancelRef, className: "clearWorkConfirmContent__btn", onClick: onCancel, children: cancelLabel }), _jsx("button", { className: "clearWorkConfirmContent__btn", onClick: onConfirm, children: confirmLabel })] }), _jsx("img", { className: "clearWorkConfirmContent__patches", src: getImageSrc("./assets/images/manipulative/patternShape/ColourPatches.svg"), alt: "" })] }));
};
/**
 * Confirmation popup rendered by ManipulativePopup.
 * Used for TWO cases via the `mode` prop:
 *   mode='clearWork'  → "Do you want to clear all of your current work…"
 *   mode='outline'    → "Do you want to change all shapes into an outline?"
 */
export var PatternOutlineConfirmationPopup = function (_a) {
    var mode = _a.mode;
    var _b = React.useContext(DataContext), manipulative = _b.manipulative, setManipulative = _b.setManipulative, setManiplulatorComponentVisible = _b.setManiplulatorComponentVisible, setActiveManipulator = _b.setActiveManipulator, selectedTool = _b.selectedTool, setSelectedTool = _b.setSelectedTool;
    var zoomLevel = globalStore.useStoreState(function (state) { return state.playerStore; }).zoomLevel;
    var setPlayerBgPattern = globalStore.useStoreActions(function (state) { return state.playerStore; }).setPlayerBgPattern;
    var dismiss = function () {
        pendingOutlineShapes = [];
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    // "Clear work" action — removes all existing pattern shapes and adds the selected outline(s).
    var onClearWork = function () {
        var shapes = pendingOutlineShapes;
        var _a = getVisibleMidpoint(zoomLevel), basex = _a.x, basey = _a.y;
        var newItems = [];
        var newSelectedToolIds = [];
        var BATCH_OFFSET = 1100;
        // Collect all existing pattern shapes that will be cleared.
        var existingShapes = manipulative.filter(function (m) { var _a; return m && ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes'; });
        var existingIds = new Set(existingShapes.map(function (s) { return s.data.id; }));
        // Build delete entries for every existing shape (full id used for both type and id so
        // the redo handler can filter by m.data.id !== itemId correctly).
        var deleteChanges = existingShapes.map(function (item) { return ({
            type: item.data.id,
            id: item.data.id,
            propertyName: 'delete',
            old: JSON.stringify(item),
        }); });
        shapes.forEach(function (shapeId, index) {
            var shape = LIBRARY_SHAPES.find(function (s) { return s.id === shapeId; });
            if (!shape || !shape.dims)
                return;
            var svgW = shape.dims.w;
            var svgH = shape.dims.h;
            var targetX = basex + index * BATCH_OFFSET;
            var targetY = basey + index * BATCH_OFFSET;
            var snappedX, snappedY;
            var gt = shape.dims.gridType;
            if (gt === 'triangle') {
                snappedY = Math.round((targetY - svgH / 2) / (2 * TRI_H)) * (2 * TRI_H);
                snappedX = Math.round((targetX - svgW / 2) / BG_GRID) * BG_GRID;
            }
            else if (gt === 'square') {
                snappedX = Math.round((targetX - svgW / 2) / BG_GRID) * BG_GRID;
                snappedY = Math.round((targetY - svgH / 2) / BG_GRID) * BG_GRID;
            }
            else {
                snappedX = targetX - svgW / 2;
                snappedY = targetY - svgH / 2;
            }
            var newItem = {
                type: 'patternShapes',
                data: {
                    id: "patternShapes_".concat(uuidv4().replace(/-/g, ''), "_dummyForRightTool"),
                    type: 'patternShapes', shapeType: 'outline',
                    center: { x: snappedX, y: snappedY },
                    rotation: 0, scale: 1,
                    svgW: svgW,
                    svgH: svgH,
                    color: 'WHITE', isOutline: true, gridType: shape.dims.gridType, path: shape.imagePath,
                    lock: { positioning: false, editing: false },
                },
                widgetId: "patternShapes:".concat(uuidv4().replace(/-/g, '')),
            };
            newItems.push(newItem);
            newSelectedToolIds.push(newItem.data.id);
        });
        // Build create entries for each new outline item.
        var createChanges = newItems.map(function (item) { return ({
            type: item.data.id,
            id: item.data.id,
            propertyName: 'create',
            old: JSON.stringify(item),
        }); });
        // Save deletes + creates as one undo batch so the whole operation reverses together.
        if (deleteChanges.length > 0 || createChanges.length > 0) {
            saveState(__spreadArray(__spreadArray([], deleteChanges, true), createChanges, true));
        }
        var firstShape = LIBRARY_SHAPES.find(function (s) { return s.id === shapes[0]; });
        if (firstShape === null || firstShape === void 0 ? void 0 : firstShape.dims) {
            var gt = firstShape.dims.gridType;
            if (gt === 'triangle')
                setPlayerBgPattern('triangle');
            else if (gt === 'square')
                setPlayerBgPattern('square');
            else
                setPlayerBgPattern('none');
        }
        // Remove existing shapes and add the new outlines in one state update.
        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !existingIds.has(m.data.id); }), true), newItems, true); });
        setSelectedTool(newSelectedToolIds);
        pendingOutlineShapes = [];
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    // "Outline" action — converts only the currently SELECTED pattern shapes into static blue shapes.
    // Uses `isStaticOutline` flag so the rendering makes stroke match fill (hiding internal borders).
    var onOutline = function () {
        var allPatternShapes = manipulative.filter(function (m) {
            return m && m.data &&
                m.data.type === 'patternShapes' &&
                !m.data.isOutline &&
                (selectedTool.includes(m.data.id) ||
                    (m.data.groupId && selectedTool.includes(m.data.groupId)));
        });
        if (allPatternShapes.length > 0) {
            var idMap_1 = {};
            allPatternShapes.forEach(function (s) {
                var sid = s.data.id;
                if (!sid.endsWith('_dummyForRightTool')) {
                    idMap_1[sid] = "".concat(sid, "_dummyForRightTool");
                }
            });
            // Build old/new snapshots outside the updater (no side effects in setManipulative).
            // We use completeData (in-place update via map) so the item keeps its position in the
            // array and z-index doesn't change.
            // The `old` snapshot explicitly includes isStaticOutline: false so that the
            // completeData merge ({...postConversionData, ...oldValue}) correctly clears it on undo
            // (without this, properties absent in the pre-conversion data are not removed by spread).
            var affectedIds_1 = new Set(allPatternShapes.map(function (s) { return s.data.id; }));
            var stateChanges = allPatternShapes.map(function (item) {
                var _a;
                var newId = (_a = idMap_1[item.data.id]) !== null && _a !== void 0 ? _a : item.data.id;
                var newItemData = __assign(__assign({}, item.data), { id: newId, isStaticOutline: true, color: 'DARK_NAVY_BLUE', lock: { positioning: true, editing: true } });
                return {
                    // type = post-conversion id so undo lookup (m.data.id === type) finds the item
                    type: newId,
                    id: newId.split('_')[1],
                    propertyName: 'completeData',
                    // Explicitly include isStaticOutline: false so spread-merge clears it on undo
                    old: JSON.stringify(__assign({ isStaticOutline: false }, item.data)),
                    new: JSON.stringify(newItemData),
                };
            });
            // In-place update via map — preserves each item's array position (z-index unchanged)
            // Strip group fields: converted outlines are standalone, no longer members of a group.
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    if (!affectedIds_1.has(item.data.id))
                        return item;
                    var newId = (_a = idMap_1[item.data.id]) !== null && _a !== void 0 ? _a : item.data.id;
                    var _b = item.data, groupId = _b.groupId, groupScale = _b.groupScale, groupPosition = _b.groupPosition, groupCenter = _b.groupCenter, beforeGroupScale = _b.beforeGroupScale, restData = __rest(_b, ["groupId", "groupScale", "groupPosition", "groupCenter", "beforeGroupScale"]);
                    return __assign(__assign({}, item), { data: __assign(__assign({}, restData), { id: newId, isStaticOutline: true, color: 'DARK_NAVY_BLUE', lock: { positioning: true, editing: true } }) });
                });
            });
            // Save as a single grouped undo entry (array = one undo step)
            saveState(stateChanges);
            // Update selectedTool: replace shape IDs with new outline IDs, and replace
            // any group IDs with the individual outline IDs of the shapes that were in that group.
            setSelectedTool(function (prev) {
                return prev.flatMap(function (t) {
                    if (idMap_1[t])
                        return [idMap_1[t]]; // direct shape ID → new outline ID
                    if (t.startsWith('manipulativeGroup_')) {
                        // Replace group ID with the outline IDs of its former members
                        var memberOutlineIds = allPatternShapes
                            .filter(function (s) { return s.data.groupId === t; })
                            .map(function (s) { var _a; return (_a = idMap_1[s.data.id]) !== null && _a !== void 0 ? _a : s.data.id; });
                        return memberOutlineIds.length > 0 ? memberOutlineIds : [t];
                    }
                    return [t];
                });
            });
        }
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    if (mode === 'clearWork') {
        return (_jsx(PatternConfirmationPopup, { heading: t('text.clearWorkConfirmHeading'), cancelLabel: t('text.cancel'), confirmLabel: t('text.clear'), onCancel: dismiss, onConfirm: onClearWork }));
    }
    return (_jsx(PatternConfirmationPopup, { heading: t('text.changeToOutlineConfirmHeading'), cancelLabel: t('text.cancel'), confirmLabel: t('text.done'), onCancel: dismiss, onConfirm: onOutline }));
};
/**
 * Confirmation popup for clearing static outline shapes.
 * Removes only the blue static outlines while keeping all other shapes.
 */
export var PatternClearOutlineConfirmationPopup = function () {
    var _a = React.useContext(DataContext), manipulative = _a.manipulative, setManipulative = _a.setManipulative, setManiplulatorComponentVisible = _a.setManiplulatorComponentVisible, setActiveManipulator = _a.setActiveManipulator;
    var dismiss = function () {
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    var onClearOutlines = function () {
        var isOutlineShape = function (m) {
            return m && m.data && m.data.type === 'patternShapes' && (m.data.isStaticOutline || m.data.isOutline);
        };
        var removedItems = manipulative.filter(isOutlineShape);
        if (removedItems.length > 0) {
            // Batch all deletions into a single undo entry so one Undo restores the whole outline.
            // `id` must be the full manipulative id so redo's filter (m.data.id !== itemId) matches.
            saveState(removedItems.map(function (item) { return ({
                type: item.data.id,
                id: item.data.id,
                propertyName: 'delete',
                old: JSON.stringify(item),
            }); }));
            setManipulative(function (prev) { return prev.filter(function (m) { return !isOutlineShape(m); }); });
        }
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    return (_jsx(PatternConfirmationPopup, { heading: t('text.clearOutlineConfirmHeading'), cancelLabel: t('text.cancel'), confirmLabel: t('text.clear'), onCancel: dismiss, onConfirm: onClearOutlines }));
};
export default PatternShapesManipulativePopup;
